import { useNavigate } from 'react-router-dom';
import { css, html } from 'react-strict-dom';
import { Api } from 'src/api';
import { ProgressBar, Text, TimerControls } from 'src/components';
import { Style } from 'src/constants';
import { color } from 'src/constants/color.css';
import { useTimer } from 'src/contexts';
import { Model } from 'src/models';

import ServiceOrderDateTrack from './ServiceOrderDateTrack';

/** A service order as the summary sends it, with its stages and time logged. */
type SummaryServiceOrder = Api.CompanyProjectSummary.V['serviceOrders'][number];

type Props = {
  serviceOrder: SummaryServiceOrder;
  company: Api.Company.BaseView;
  /** Sits under the project it belongs to, with a rail tying it there. */
  nested?: boolean;
};

/**
 * One service order, as a bordered block so the control on the right is
 * unmistakably part of the name beside it.  The block is in two halves: the
 * order as a whole above the divider, the stage in flight below it.
 */
export default function ProjectsServiceOrderRow({
  serviceOrder,
  company,
  nested,
}: Props) {
  const navigate = useNavigate();
  const target = Model.TimerTarget.fromServiceOrder(serviceOrder, company);
  const running = useTimer().isRunning(target);

  const fee = Model.ServiceOrder.formatFee(serviceOrder.budget);
  const days = Model.ServiceOrder.getDayLimitProgress(
    serviceOrder,
    serviceOrder.totalMinutesLogged
  );
  const stage = Model.ServiceOrder.getNextStage(serviceOrder.stages);
  const dateTrack = Model.ServiceOrder.getDateTrack(
    serviceOrder.stages,
    new Date()
  );

  return (
    <html.div style={[Style.row, styles.row, nested && styles.nested]}>
      {nested && <html.span style={styles.rail} />}

      <html.div
        style={[
          Style.col,
          styles.block,
          running && styles.running,
          styles.clickable,
        ]}
        onClick={() => navigate(`/projects/service-orders/${serviceOrder.id}`)}
      >
        <html.div style={[Style.row, Style.alignCenter, styles.head]}>
          <html.div style={[Style.col, styles.label]}>
            <html.div style={[Style.row, Style.alignCenter, styles.nameRow]}>
              <Text typeface="body.small.medium" color="neutral100">
                {target.title}
              </Text>
              {serviceOrder.referenceNumber && (
                <Text typeface="caption.medium" color="neutral60">
                  {`#${serviceOrder.referenceNumber}`}
                </Text>
              )}
            </html.div>

            {/* What the whole order is worth, so it sits under the title
                rather than with the stage in flight below. */}
            {(fee !== null || days !== null) && (
              <html.div style={[Style.row, Style.alignCenter, styles.summary]}>
                {fee !== null && (
                  <Text typeface="caption.medium" color="neutral60">
                    {fee}
                  </Text>
                )}
                {days !== null && (
                  <ProgressBar progress={days.progress} label={days.label} />
                )}
              </html.div>
            )}
          </html.div>

          <html.span onClick={(e) => e.stopPropagation()} style={Style.shrink0}>
            <TimerControls target={target} />
          </html.span>
        </html.div>

        {stage !== null && (
          <html.div style={[Style.col, styles.stage]}>
            <html.span style={styles.divider} />
            <html.div style={[Style.row, Style.alignCenter, styles.stageRow]}>
              <Text typeface="caption.medium" color="neutral60">
                Current stage:
              </Text>
              <Text typeface="body.small.medium" color="neutral100">
                {stage.name}
              </Text>
            </html.div>
            {dateTrack !== null && <ServiceOrderDateTrack track={dateTrack} />}
          </html.div>
        )}
      </html.div>
    </html.div>
  );
}

const styles = css.create({
  row: {
    position: 'relative',
    paddingBlock: 4,
    paddingRight: 16,
    // Level with the section heading above, so the card has one left margin.
    paddingLeft: 16,
  },
  nested: {
    paddingLeft: 36,
  },
  // Ties a service order to the project above it, running the row's full
  // height so consecutive ones read as one bracket.
  rail: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 24,
    width: 1,
    backgroundColor: color.neutral20,
  },
  block: {
    flexGrow: 1,
    minWidth: 0,
    gap: 8,
    padding: {
      default: 12,
      '@media (max-width: 640px)': 10,
    },
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    backgroundColor: color.neutral0,
  },
  running: {
    backgroundColor: color.greenBg,
  },
  clickable: {
    cursor: 'pointer',
    ':hover': {
      borderColor: color.neutral40,
    },
  },
  head: {
    gap: {
      default: 12,
      '@media (max-width: 640px)': 10,
    },
  },
  label: {
    flexGrow: 1,
    minWidth: 0,
    gap: 2,
  },
  nameRow: {
    gap: 6,
    minWidth: 0,
  },
  summary: {
    gap: 12,
    flexWrap: 'wrap',
  },
  stage: {
    gap: 8,
  },
  divider: {
    height: 1,
    backgroundColor: color.neutral20,
  },
  stageRow: {
    gap: 6,
  },
});
